import { useState, useCallback } from 'react';
import html2canvas from 'html2canvas';

export const useLogoGenerator = () => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(async (format: 'png' | 'svg' = 'png') => {
    const element = document.getElementById('logo-preview');
    if (!element) return;

    setIsExporting(true);
    try {
      if (format === 'svg') {
        // Get the SVG content
        const svgElement = element.querySelector('.logo-content');
        if (!svgElement) throw new Error('SVG element not found');

        // Create SVG with proper viewBox and transparent background
        const svgContent = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svgElement.clientWidth} ${svgElement.clientHeight}">
            <defs>
              ${Array.from(svgElement.querySelectorAll('defs')).map(def => def.innerHTML).join('')}
            </defs>
            <style>
              @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
              text { font-family: 'Inter', sans-serif; }
            </style>
            ${svgElement.innerHTML}
          </svg>
        `;

        // Create and download SVG file
        const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'scalenix-logo.svg';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        // PNG export with transparency
        const canvas = await html2canvas(element, {
          backgroundColor: null, // Ensure transparent background
          scale: 2, // Higher resolution
          logging: false,
          useCORS: true,
          allowTaint: true,
          onclone: (clonedDoc) => {
            // Fix gradient text rendering
            const clonedElement = clonedDoc.getElementById('logo-preview');
            if (clonedElement) {
              const textElements = clonedElement.querySelectorAll('[style*="background"]');
              textElements.forEach((el: Element) => {
                if (el instanceof HTMLElement) {
                  const style = window.getComputedStyle(el);
                  el.style.color = style.backgroundImage;
                  el.style.backgroundImage = 'none';
                }
              });
            }
          }
        });

        // Convert to PNG with transparency
        const link = document.createElement('a');
        link.download = 'scalenix-logo.png';
        link.href = canvas.toDataURL('image/png');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Failed to export logo:', error);
    } finally {
      setIsExporting(false);
    }
  }, []);

  return {
    handleExport,
    isExporting
  };
};